/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial creation of the WhitelabelContext and Provider.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Added `isHeaderConfigActive` to the context to indicate
 *                                   if the whitelabel configuration is being overridden by an API header.
 *                                   Modified `updateWhitelabelConfig` and `resetWhitelabelConfig` to
 *                                   respect `isHeaderConfigActive`.
 *
 * -----
 * SECURITY_RISK: LOW - This context provides access to the UI whitelabel configuration.
 *                      It does not handle sensitive data directly.
 * INTENT: Provide a global React Context for whitelabeling settings, allowing
 *         components throughout the application to access and update the configuration
 *         without prop drilling.
 * PROMPTER_INSTRUCTIONS: Ensure the context correctly wraps the `useWhitelabel` hook
 *                        and exposes the necessary state and functions.
 * LLM_CONFIDENCE: 100% - Standard React Context implementation.
 */
import React, { createContext, useContext, ReactNode } from 'react';
import { useWhitelabel } from '../hooks/useWhitelabel.ts';
import { WhitelabelConfig } from '../types/whitelabel.d.ts';

// Define the shape of the context value
interface WhitelabelContextType {
  whitelabelConfig: WhitelabelConfig;
  loading: boolean;
  error: string | null;
  isHeaderConfigActive: boolean; // NEW: Indicates if the config is from an HTTP header
  updateWhitelabelConfig: (newConfig: Partial<WhitelabelConfig>) => Promise<void>;
  resetWhitelabelConfig: () => Promise<void>;
}

// Create the context with a default undefined value
const WhitelabelContext = createContext<WhitelabelContextType | undefined>(undefined);

// Create a provider component
export const WhitelabelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const whitelabelHook = useWhitelabel(); // Call the hook once here

  return (
    <WhitelabelContext.Provider value={whitelabelHook}>
      {children}
    </WhitelabelContext.Provider>
  );
};

// Create a custom hook to use the WhitelabelContext
export const useWhitelabelContext = () => {
  const context = useContext(WhitelabelContext);
  if (context === undefined) {
    throw new Error('useWhitelabelContext must be used within a WhitelabelProvider');
  }
  return context;
};
